import React from "react";
import { Link } from "react-router-dom";
import { useT } from "@/lib/LanguageContext";
import { crmHref } from "../paths";
import CrmSwitch from "../ui/CrmSwitch";
import { LABEL } from "../ui/classes";
import ConsentTextField from "./ConsentTextField";
import EmbedCodeBlock from "./EmbedCodeBlock";
import { CRM } from "../../../constants/testIds/crm";

/** One toggleable field row (design §E.4): Name and Email are fixed on ("[required] ●"). */
function FieldRow({ testId, label, fixed, checked, onChange, hint }) {
  const t = useT("crm");
  return (
    <div className="flex items-center justify-between gap-3 py-2">
      <span className="text-sm text-eh-ink">
        {label}
        {fixed && <span className="ml-1.5 text-xs text-eh-muted">{t("forms.required")} ●</span>}
      </span>
      {!fixed && <CrmSwitch checked={checked} onCheckedChange={onChange} data-testid={testId} aria-label={label} />}
      {hint && <span className="text-xs text-eh-muted">{hint}</span>}
    </div>
  );
}

/**
 * The Forms page's editing column (design §E.4): field toggles, the editable consent
 * text and the embed snippet. Every toggle and the consent text autosave individually
 * (`onFieldChange` / `onConsentChange` call `PUT /api/crm/forms/{id}` per change) rather
 * than behind one "Save" — the wireframe shows no save button on this screen.
 */
export default function FormFieldsCard({ form, submissionsCount, onFieldChange, onConsentChange }) {
  const t = useT("crm");
  const fields = form.fields || {};
  const intake = fields.intake || {};

  return (
    <div className="space-y-6">
      <Link to={crmHref("/clients?source=form")} className="text-sm font-medium text-eh-ink underline-offset-4 hover:underline" data-testid={CRM.formsSubmissions}>
        {t("forms.submissions", { n: submissionsCount ?? 0 })}
      </Link>

      <div>
        <span className={LABEL}>{t("forms.fields")}</span>
        <FieldRow label={t("forms.fieldName")} fixed />
        <FieldRow label={t("forms.fieldEmail")} fixed />
        <FieldRow
          label={t("forms.fieldPhone")}
          checked={!!fields.phone}
          onChange={(v) => onFieldChange({ ...fields, phone: v })}
          testId={CRM.formsToggle("phone")}
        />
        <FieldRow
          label={t("forms.fieldMessage")}
          checked={!!fields.message}
          onChange={(v) => onFieldChange({ ...fields, message: v })}
          testId={CRM.formsToggle("message")}
        />
        <FieldRow
          label={t("forms.fieldIntake")}
          checked={!!intake.enabled}
          onChange={(v) => onFieldChange({ ...fields, intake: { ...intake, enabled: v } })}
          testId={CRM.formsToggle("intake")}
        />
      </div>

      <ConsentTextField value={form.consent_text} onSave={onConsentChange} />

      <EmbedCodeBlock snippet={form.embed_snippet} />
    </div>
  );
}
